/**
 * Add Device Type Form Composer
 *
 * Form for creating new device types with manufacturer selection.
 * deviceTypeNumber is auto-generated on submission.
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddDeviceTypeFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddDeviceTypeFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue } from '@/lib/utils'
import { AppLogger } from '@/lib/logger'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Tag } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

// Component Props
// ============================================================================

export type AddDeviceTypeComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddDeviceTypeFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Constants
// ============================================================================


// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof AddDeviceTypeFormData, value: unknown): string | null => {
  const shape = AddDeviceTypeFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function AddDeviceTypeComposer(props: AddDeviceTypeComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['deviceTypeName', 'deviceTypeManufacturerNumber', 'deviceTypeModel'].includes(name)
  }

  // Fetch all select options with a single hook call
  const { optionsMap, loadingMap } = useSelectOptionsMap({
    deviceTypeManufacturerNumber: {
      endpoint: 'getDeviceManufacturers',
      dataKey: 'manufacturers',
      valueField: 'manufacturerNumber',
      labelField: 'manufacturerName',
    },
  })

  const upsertDeviceType = useApiMutation('upsertDeviceType', 'POST', {
    invalidates: ['getDeviceTypes'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create device type:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddDeviceTypeFormSchema,
    defaultValues: {
      deviceTypeNumber: initialValues?.deviceTypeNumber,
      deviceTypeName: initialValues?.deviceTypeName ?? '',
      deviceTypeManufacturerNumber: initialValues?.deviceTypeManufacturerNumber ?? ('' as any),
      deviceTypeModel: initialValues?.deviceTypeModel ?? '',
      deviceTypeCategory: initialValues?.deviceTypeCategory ?? undefined,
      deviceTypeStatus: initialValues?.deviceTypeStatus ?? undefined,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddDeviceTypeFormData
      // Coerce and validate with Zod schema (single source of truth)
      const coerced = {
        deviceTypeNumber: initialValues?.deviceTypeNumber,
        deviceTypeName: formValue.deviceTypeName?.trim() ?? '',
        deviceTypeManufacturerNumber: formValue.deviceTypeManufacturerNumber
          ? Number(formValue.deviceTypeManufacturerNumber)
          : undefined,
        deviceTypeModel: formValue.deviceTypeModel?.trim() ?? '',
        deviceTypeCategory: formValue.deviceTypeCategory || undefined,
        deviceTypeStatus: formValue.deviceTypeStatus || undefined,
      }

      const parsed = AddDeviceTypeFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof AddDeviceTypeFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await upsertDeviceType.mutateAsync(apiRequest)
    },
  })

  // Render a single text control
  const renderTextControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddDeviceTypeFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render a single select control
  const renderSelectControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)
    const isLoading = loadingMap[control.name]

    // Get options from API or YAML
    const options = control.optionsEndpoint
      ? optionsMap[control.name] || []
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddDeviceTypeFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: options,
            isLoading: isLoading,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Universal render function that dispatches to specific renderers
  const renderControl = (control: any) => {
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control)
    }
    return renderTextControlField(control)
  }

  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  const formBody = (
    <>
        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: upsertDeviceType.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Submitting...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Submit'),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <Tag className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}

// Re-exports
export {
  AddDeviceTypeFormSchema,
  transformToApiRequest,
  DeviceTypeStatusEnum,
  DeviceTypeCategoryEnum,
  FORM_CONFIG,
  type AddDeviceTypeFormData,
  type DeviceTypeStatus,
  type DeviceTypeCategory,
} from './schema'
